import { Button, FestiveButton, HeadingTag, Link, Media, ModuleBase, ResponsiveImage } from '@/components';
import { selectIsCampaignActive } from '@/store';
import { isColorDark, themeFromBg } from '@/utils';
import Flicking from '@egjs/react-flicking';
import classNames from 'classnames';
import { useSelector } from 'react-redux';

const GiftsModule = ({ data, ...props }) => {
  const campaignActive = useSelector(selectIsCampaignActive);

  return (
    <ModuleBase
      data={data}
      {...props}
      theme={themeFromBg(!isColorDark(data.backgroundColour))}
      className={campaignActive && data.campaignMode ? 'campaign' : ''}
    >
      <div
        className={classNames(
          'container flex gap-6 lg:gap-[5.5rem] items-center justify-start flex-col-reverse pr-0 md:pr-6',
          data.mediaAlignment === 'Left' ? 'lg:pl-0 lg:flex-row' : 'lg:pr-0 lg:flex-row-reverse',
        )}
      >
        <Media
          media={data.mainImage}
          widths={{ xs: 608, sm: 736, md: 976, lg: 703, xl: 703, xxl: 703 }}
          heights={{ xs: 343, sm: 343, md: 343, lg: 527, xl: 527, xxl: 527 }}
          className="block h-[343px] lg:h-[527px] lg:max-w-[703px] pr-4 md:pr-0"
        />
        <div className="lg:py-[2.125rem] w-full lg:max-w-[548px] color-from-bg">
          {data.title && (
            <HeadingTag
              data={data.title}
              className="text-headingReg -tracking-[0.01em] lg:text-title-l lg:leading-60 lg:-tracking-[0.03em] font-ivy-ora mb-6 pr-4 md:pr-0"
            />
          )}
          {data.price && (
            <p className="text-large-paragraph lg:text-paragraph-l lg:leading-6 -tracking-[0.02em] mb-6 lg:mb-8 pr-4 md:pr-0">
              {data.price}
            </p>
          )}
          {data.description && (
            <div
              className="text-large-paragraph mb-6 lg:mb-8 lg:max-w-[332px] pr-4 md:pr-0"
              dangerouslySetInnerHTML={{ __html: data.description }}
            />
          )}
          {data.giftItems && data.giftItems.length && (
            <>
              {data.giftItems.length === 3 && (
                <Flicking
                  horizontal
                  align="prev"
                  preventClickOnDrag
                  preventDefaultOnDrag
                  className="md:hidden overflow-visible pr-2 mb-6"
                >
                  {data.giftItems.map((gift, index) => (
                    <div key={`gift-${index}`} className="basis-[calc(100%/2.1)] max-w-[165px] flex mr-2 last:mr-0">
                      {gift.primaryCta ? (
                        <Link link={gift.primaryCta} className="size-full">
                          <ResponsiveImage
                            image={gift.image}
                            widths={{ xs: 248, sm: 248, md: 248, lg: 248, xl: 248, xxl: 248 }}
                            heights={{ xs: 165, sm: 165, md: 165, lg: 165, xl: 165, xxl: 165 }}
                            className="block !h-[110px]"
                          />
                          <p
                            className="text-paragraph-m leading-[1.375rem] mt-2"
                            dangerouslySetInnerHTML={{ __html: gift.description }}
                          />
                        </Link>
                      ) : (
                        <div>
                          <ResponsiveImage
                            image={gift.image}
                            widths={{ xs: 248, sm: 248, md: 248, lg: 248, xl: 248, xxl: 248 }}
                            heights={{ xs: 165, sm: 165, md: 165, lg: 165, xl: 165, xxl: 165 }}
                            className="block !h-[110px]"
                          />
                          <p
                            className="text-paragraph-m leading-[1.375rem] mt-2"
                            dangerouslySetInnerHTML={{ __html: gift.description }}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </Flicking>
              )}
              <div
                className={classNames(
                  'gap-2 md:gap-4 mb-6 lg:mb-8',
                  data.giftItems.length === 3 ? 'hidden md:flex' : 'flex',
                )}
              >
                {data.giftItems.map((gift, index) =>
                  gift.primaryCta ? (
                    <Link
                      link={gift.primaryCta}
                      key={`gift-${index}`}
                      className={classNames(data.giftItems.length === 2 ? 'w-[165px] lg:w-[230px]' : 'w-[165px]')}
                    >
                      <ResponsiveImage
                        image={gift.image}
                        widths={
                          data.giftItems.length === 2
                            ? { xs: 345, sm: 345, md: 345, lg: 345, xl: 345, xxl: 345 }
                            : { xs: 248, sm: 248, md: 248, lg: 248, xl: 248, xxl: 248 }
                        }
                        heights={
                          data.giftItems.length === 2
                            ? { xs: 248, sm: 248, md: 248, lg: 248, xl: 248, xxl: 248 }
                            : { xs: 165, sm: 165, md: 165, lg: 165, xl: 165, xxl: 165 }
                        }
                        className={classNames(
                          'block',
                          data.giftItems.length === 2 ? '!h-[110px] lg:!h-[165px]' : '!h-[110px]',
                        )}
                      />
                      <p
                        className="text-paragraph-m leading-[1.375rem] mt-2"
                        dangerouslySetInnerHTML={{ __html: gift.description }}
                      />
                    </Link>
                  ) : (
                    <div
                      key={`gift-${index}`}
                      className={classNames(data.giftItems.length === 2 ? 'w-[165px] lg:w-[230px]' : 'w-[165px]')}
                    >
                      <ResponsiveImage
                        image={gift.image}
                        widths={
                          data.giftItems.length === 2
                            ? { xs: 345, sm: 345, md: 345, lg: 345, xl: 345, xxl: 345 }
                            : { xs: 248, sm: 248, md: 248, lg: 248, xl: 248, xxl: 248 }
                        }
                        heights={
                          data.giftItems.length === 2
                            ? { xs: 248, sm: 248, md: 248, lg: 248, xl: 248, xxl: 248 }
                            : { xs: 165, sm: 165, md: 165, lg: 165, xl: 165, xxl: 165 }
                        }
                        className={classNames(
                          'block',
                          data.giftItems.length === 2 ? '!h-[110px] lg:!h-[165px]' : '!h-[110px]',
                        )}
                      />
                      <p
                        className="text-paragraph-m leading-[1.375rem] mt-2"
                        dangerouslySetInnerHTML={{ __html: gift.description }}
                      />
                    </div>
                  ),
                )}
              </div>
            </>
          )}
          {data.primaryCta ? (
            campaignActive && data.campaignMode ? (
              <FestiveButton link={data.primaryCta}>{data.primaryCta.text}</FestiveButton>
            ) : (
              <Button theme={themeFromBg(isColorDark(data.backgroundColour))} link={data.primaryCta} type="primary" />
            )
          ) : null}
        </div>
      </div>
    </ModuleBase>
  );
};

export default GiftsModule;
